/** @prettier */
//-------------------------------------
//	グラフ表示項目の選択ダイアログ
//-------------------------------------

import {
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Dialog,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
} from '@mui/material';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

//interface import
import * as apiIF from '../Type/api';
import {PrimaryButton, SecondaryButton} from './BasicButton';
import {graph} from '../define';

/**
 * グラフ表示項目の選択ダイアログ（Chiller）
 * @param props props
 * - activeComparison 比較グラフ表示判定
 * - isDialogOpen グラフ表示項目選択ダイアログ開閉
 * - setIsDialogOpen グラフ表示項目選択ダイアログ開閉設定関数
 * - selectedItem グラフ表示項目
 * - setSelectedItem グラフ表示項目設定関数
 * @returns JSX
 */
const ItemGraphDialogChiller = (props: {
  activeComparison: boolean;
  isDialogOpen: boolean;
  setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedItem: (keyof apiIF.resParameters.rtOUStates)[];
  setSelectedItem: React.Dispatch<
    React.SetStateAction<(keyof apiIF.resParameters.rtOUStates)[]>
  >;
}) => {
  const trans = useTranslation().t; //翻訳関数

  //温度データ項目
  const tempItem: {
    name: keyof apiIF.resParameters.rtOUStates;
    label: string;
  }[] = [
    {
      name: 'inlet-water-temperature',
      label: trans('ItemGraphDialogChiller.tempDataItemList.item.inletTemp'),
    },
    {
      name: 'outlet-water-temperature',
      label: trans('ItemGraphDialogChiller.tempDataItemList.item.outletTemp'),
    },
  ];
  //圧力データ項目
  const pressureItem: {
    name: keyof apiIF.resParameters.rtOUStates;
    label: string;
  }[] = [
    {
      name: 'high-pressure',
      label: trans(
        'ItemGraphDialogChiller.compressorDataItemList.item.compressor1HighPressure'
      ),
    },
    {
      name: 'low-pressure',
      label: trans(
        'ItemGraphDialogChiller.compressorDataItemList.item.compressor1LowPressure'
      ),
    },
    {
      name: 'high-pressure2',
      label: trans(
        'ItemGraphDialogChiller.compressorDataItemList.item.compressor2HighPressure'
      ),
    },
    {
      name: 'low-pressure2',
      label: trans(
        'ItemGraphDialogChiller.compressorDataItemList.item.compressor2LowPressure'
      ),
    },
  ];
  //量データ項目
  const amountItem: {
    name: keyof apiIF.resParameters.rtOUStates;
    label: string;
  }[] = [
    {
      name: 'flow-rate',
      label: trans(
        'ItemGraphDialogChiller.hotWaterStorageAmountDataItemList.item.flowRate'
      ),
    },
  ];

  //選択した温度データ項目
  const [selectedTempItem, setSelectedTempItem] = useState<
    (keyof apiIF.resParameters.rtOUStates)[]
  >(
    props.selectedItem.filter(item => {
      return tempItem.some(info => info.name === item);
    })
  );
  //選択した圧力データ項目
  const [selectedPressureItem, setSelectedPressureItem] = useState<
    (keyof apiIF.resParameters.rtOUStates)[]
  >(
    props.selectedItem.filter(item => {
      return pressureItem.some(info => info.name === item);
    })
  );
  //選択した量データ項目
  const [selectedAmountItem, setSelectedAmountItem] = useState<
    (keyof apiIF.resParameters.rtOUStates)[]
  >(
    props.selectedItem.filter(item => {
      return amountItem.some(info => info.name === item);
    })
  );

  //比較グラフに変更した際の選択項目数の変化を、チェックボックスに反映するための処理
  useEffect(() => {
    setSelectedTempItem(
      props.selectedItem.filter(item => {
        return tempItem.some(info => info.name === item);
      })
    );
    setSelectedPressureItem(
      props.selectedItem.filter(item => {
        return pressureItem.some(info => info.name === item);
      })
    );
    setSelectedAmountItem(
      props.selectedItem.filter(item => {
        return amountItem.some(info => info.name === item);
      })
    );
  }, [props.selectedItem]);

  /**
   * 選択する項目のdisable判定
   * @param selectedItems 選択した項目
   * @param itemName 項目の名前
   * @returns boolean
   */
  const selectedItemDisable = (
    selectedItems: (keyof apiIF.resParameters.rtOUStates)[],
    itemName: keyof apiIF.resParameters.rtOUStates
  ) => {
    /** 各項目の合計選択数 */
    const selectedItemsLength =
      selectedTempItem.length +
      selectedPressureItem.length +
      selectedAmountItem.length;

    if (props.activeComparison) {
      //比較グラフ時、選択されている項目が3つの場合、チェックボックスが選択済みでない項目をdisableとする
      return (
        selectedItemsLength === graph.COMPARISON_ITEM_MAX_NUMBER &&
        selectedItems.includes(itemName) === false
      );
    } else {
      //通常グラフ時、選択されている項目数が4つの場合、チェックボックスが選択済ではない項目をdisableとする
      return (
        selectedItemsLength === graph.ITEM_MAX_NUMBER &&
        selectedItems.includes(itemName) === false
      );
    }
  };

  /**
   * 温度データ項目変更時処理
   * @param event
   */
  const handleChangeTempItem = (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetName = event.target
      .name as keyof apiIF.resParameters.rtOUStates;
    if (selectedTempItem.includes(targetName)) {
      setSelectedTempItem(selectedTempItem.filter(item => item !== targetName));
    } else {
      setSelectedTempItem([...selectedTempItem, targetName]);
    }
  };
  /**
   * 圧力データ項目変更時処理
   * @param event
   */
  const handleChangePressureItem = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const targetName = event.target
      .name as keyof apiIF.resParameters.rtOUStates;
    if (selectedPressureItem.includes(targetName)) {
      setSelectedPressureItem(
        selectedPressureItem.filter(item => item !== targetName)
      );
    } else {
      setSelectedPressureItem([...selectedPressureItem, targetName]);
    }
  };
  /**
   * 量データ項目変更時処理
   * @param event
   */
  const handleChangeAmountItem = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const targetName = event.target
      .name as keyof apiIF.resParameters.rtOUStates;
    if (selectedAmountItem.includes(targetName)) {
      setSelectedAmountItem(
        selectedAmountItem.filter(item => item !== targetName)
      );
    } else {
      setSelectedAmountItem([...selectedAmountItem, targetName]);
    }
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    setSelectedTempItem(
      props.selectedItem.filter(item => {
        return tempItem.some(info => info.name === item);
      })
    );
    setSelectedPressureItem(
      props.selectedItem.filter(item => {
        return pressureItem.some(info => info.name === item);
      })
    );
    setSelectedAmountItem(
      props.selectedItem.filter(item => {
        return amountItem.some(info => info.name === item);
      })
    );
    props.setIsDialogOpen(false);
  };
  /**
   * OKボタン押下時処理
   */
  const handleSave = () => {
    let tempSelectedItem: (keyof apiIF.resParameters.rtOUStates)[] = [];

    if (selectedTempItem.length > 0) {
      tempSelectedItem = [...tempSelectedItem, ...selectedTempItem];
    }
    if (selectedPressureItem.length > 0) {
      tempSelectedItem = [...tempSelectedItem, ...selectedPressureItem];
    }
    if (selectedAmountItem.length > 0) {
      tempSelectedItem = [...tempSelectedItem, ...selectedAmountItem];
    }
    props.setSelectedItem(tempSelectedItem);
    props.setIsDialogOpen(false);
  };

  return (
    <Dialog
      open={props.isDialogOpen}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      fullWidth={true}
      maxWidth="md">
      <DialogTitle id="form-dialog-title">
        {trans('ItemGraphDialogChiller.title')}
      </DialogTitle>
      <DialogContent>
        {/* ダイアログに表示するメッセージ */}
        <DialogContentText>
          {trans('ItemGraphDialogChiller.msg')}
        </DialogContentText>
        <Grid container columnSpacing={5}>
          <Grid item xs={4}>
            <Typography>
              {trans('ItemGraphDialogChiller.tempDataItemList.heading')}
            </Typography>
            <Card variant="outlined">
              <CardContent>
                <FormControl>
                  <FormGroup>
                    {tempItem.map(info => (
                      <FormControlLabel
                        key={`temperature-${info.name}`}
                        control={
                          <Checkbox
                            checked={selectedTempItem.includes(info.name)}
                            onChange={handleChangeTempItem}
                            name={info.name}
                            disabled={selectedItemDisable(
                              selectedTempItem,
                              info.name
                            )}
                          />
                        }
                        label={info.label}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Typography>
              {trans('ItemGraphDialogChiller.compressorDataItemList.heading')}
            </Typography>
            <Card variant="outlined">
              <CardContent>
                <FormControl>
                  <FormGroup>
                    {pressureItem.map(info => (
                      <FormControlLabel
                        key={`pressure-${info.name}`}
                        control={
                          <Checkbox
                            checked={selectedPressureItem.includes(info.name)}
                            onChange={handleChangePressureItem}
                            name={info.name}
                            disabled={selectedItemDisable(
                              selectedPressureItem,
                              info.name
                            )}
                          />
                        }
                        label={info.label}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Typography>
              {trans(
                'ItemGraphDialogChiller.hotWaterStorageAmountDataItemList.heading'
              )}
            </Typography>
            <Card variant="outlined">
              <CardContent>
                <FormControl>
                  <FormGroup>
                    {amountItem.map(info => (
                      <FormControlLabel
                        key={`amount-${info.name}`}
                        control={
                          <Checkbox
                            checked={selectedAmountItem.includes(info.name)}
                            onChange={handleChangeAmountItem}
                            name={info.name}
                            disabled={selectedItemDisable(
                              selectedAmountItem,
                              info.name
                            )}
                          />
                        }
                        label={info.label}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <SecondaryButton
          onClick={handleClose}
          buttonText={trans('ItemGraphDialogChiller.buttonTxt.cancel')}
        />
        <PrimaryButton
          onClick={handleSave}
          buttonText={trans('ItemGraphDialogChiller.buttonTxt.ok')}
        />
      </DialogActions>
    </Dialog>
  );
};
export default ItemGraphDialogChiller;
